import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'

import { BaseRecord } from '../../models/BaseRecord'
import { FEATURE, Feature } from '../../models/Feature'
import { Package } from '../../models/Package'
import { RecordFactory } from '../../models/RecordFactory'
import { Taxcode } from '../../models/Taxcode'
import { User } from '../../models/User'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import { PackageView } from './PackageView'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { NewPackagePage } from './NewPackagePage'
import { NewPackageCompletePage } from './NewPackageCompletePage'

// Note: the variable name `packageObj` is used in this entire component instead of
//       `package` because `package` is a keyword.

const viewWillAppear = async ({
    owner,
    urlParams,
    me,
    props: { action },
}: PocketComponent<{ action: 'new' | 'edit' | 'copy' }>) => {
    const packageItems = await RecordFactory.getInstance().loadItems(owner.id ?? '', 'package')

    const defaultPackageObj = await (async (): Promise<Package | null> => {
        switch (action) {
            case 'new': {
                return new Package({
                    owner,
                    client: undefined,
                    reminder: owner.allowReminder ? owner.invoiceReminder : undefined,
                    employee:
                        Feature.enabled(FEATURE.FEATURE_STAFFING, owner) &&
                        me.payee &&
                        me.perm === User.PERM_ROLE_EMPLOYEE
                            ? me
                            : undefined,
                    allow_surcharge: owner.allowSurcharge,
                    ...(Taxcode.defaultTaxable(owner)
                        ? {
                              nexus: 'pro',
                              tax: owner.taxcode?.rate,
                              taxcode: owner.taxcode,
                          }
                        : {}),
                    status: 'sentQuote',
                    channel: owner.channel,
                    capacity: 1,
                    expires: 7,
                })
            }
            case 'edit': {
                if (!urlParams.id) {
                    return null
                }
                return (await RecordFactory.getInstance().loadPackage(urlParams.id)) ?? null
            }
            case 'copy': {
                if (!urlParams.id) {
                    return null
                }
                const packageObj =
                    (await RecordFactory.getInstance().loadPackage(urlParams.id)) ?? null
                if (!packageObj) {
                    return null
                }
                return packageObj.clone({ id: undefined })
            }
        }
    })()
    if (!defaultPackageObj || defaultPackageObj.owner?.id !== owner.id) {
        return new RecordNotFoundError()
    }

    return { defaultPackageObj, packageItems }
}

const NewPackageWorkflowRoot = ({
    data,
    base: { owner, logAndDisplayError },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { defaultPackageObj, packageItems } = data

    const [searchParams, setSearchParams] = useSearchParams()

    const stepIndex = useMemo(() => {
        switch (searchParams.get('step')) {
            case 'review':
                return 1
            case 'success':
                return 2
            default:
                return 0
        }
    }, [searchParams])

    const isEditing = defaultPackageObj.id !== undefined

    const navigate = useNavigate()

    const [packageObj, setPackageObj] = useState(defaultPackageObj)

    const [loadingButton, setLoadingButton] = useState<'save' | 'charge' | 'send' | null>(null)

    const canAdvanceToStep2 = packageObj.client !== undefined && packageObj.item !== undefined

    WorkflowPage.useNavigationHandlers({
        stepIndex,
        isEditing,
        canAdvanceToStep2,
        recordID: packageObj.id,
    })

    const hasUnsavedChanges =
        stepIndex <= 1 && BaseRecord.hasChanges(data.defaultPackageObj, packageObj)

    const onSave = async (shouldSend: boolean, shouldCharge: boolean) => {
        setLoadingButton(
            !shouldSend && !shouldCharge ? 'save' : shouldSend && shouldCharge ? 'charge' : 'send'
        )

        const result = await RecordFactory.getInstance().sendPackage(packageObj, !shouldSend)
        setLoadingButton(null)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        setPackageObj(result.value)

        if (shouldCharge) {
            navigate(`/charge/package/${result.value.id}`)
        }
        else {
            setSearchParams({ step: 'success' }, { replace: true })
        }
    }

    return (
        <>
            <WorkflowPage
                title={stepIndex === 0 ? 'Sell package' : stepIndex === 1 ? 'Review' : undefined}
                backButtonTitle={
                    stepIndex === 1 ? (isEditing ? 'Edit Package' : 'Create Package') : undefined
                }
                onBack={() => navigate(-1)}
                steps={[isEditing ? 'Edit' : 'Create', 'Review', 'Save']}
                currentStepIndex={stepIndex}
                shouldForceCurrentStepCheckmark={stepIndex === 2}
                onClose={{
                    action: 'go-back',
                    excludePathname: /^\/packages.*\/(new|edit|copy)/,
                    defaultPathname: '/dashboards/packages',
                }}
                footerContent={
                    stepIndex === 0 || stepIndex === 1 ? (
                        <div className="flex w-full items-center justify-end gap-x-4">
                            {stepIndex === 0 ? (
                                <Button
                                    intent="primary"
                                    disabled={!canAdvanceToStep2}
                                    onClick={() => setSearchParams({ step: 'review' })}
                                >
                                    Next
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        intent="secondary"
                                        onClick={() => onSave(false, false)}
                                        isLoading={loadingButton === 'save'}
                                    >
                                        Save
                                    </Button>
                                    {!packageObj.paidDate && (
                                        <Button
                                            intent="secondary"
                                            onClick={() => onSave(true, true)}
                                            isLoading={loadingButton === 'charge'}
                                        >
                                            {packageObj.chargeAmount === 0 ? 'Mark Paid' : 'Charge'}
                                        </Button>
                                    )}
                                    {!!packageObj.channel && (
                                        <Button
                                            intent="primary"
                                            onClick={() => onSave(true, false)}
                                            isLoading={loadingButton === 'send'}
                                        >
                                            Send
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    ) : undefined
                }
                shouldVerticallyCenterContent={stepIndex === 2}
            >
                {stepIndex === 0 ? (
                    <NewPackagePage
                        packageObj={packageObj}
                        onUpdatePackage={setPackageObj}
                        owner={owner}
                        packageItems={packageItems}
                    />
                ) : stepIndex === 1 &&
                  packageObj.client !== undefined &&
                  packageObj.item !== undefined ? (
                    <div className="mt-6">
                        <PackageView
                            packageObj={packageObj}
                            isWorkflowReviewStep
                            client={packageObj.client}
                            item={packageObj.item}
                        />
                    </div>
                ) : stepIndex === 2 ? (
                    <NewPackageCompletePage packageObj={packageObj} />
                ) : null}
            </WorkflowPage>

            <WorkflowPage.NavigationBlocker
                when={hasUnsavedChanges}
                allowNextPaths={[/charge\/package\/.*/]}
            />
        </>
    )
}

const render = (props: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    return (
        <WorkflowPage.ResetContainer>
            <NewPackageWorkflowRoot {...props} />
        </WorkflowPage.ResetContainer>
    )
}

export const PackageWorkflow = usePSComponent(render, viewWillAppear)
